import { useQuery, useTranslation } from '@gimlite/common';
import { Icon, IconType, Row, Widget, Write } from '@gimlite/ui-v1';
import { observer } from 'mobx-react-lite';
import {
  Query,
  QueryParkingArgs,
  QueryTotalSpacesArgs,
} from '../../client/graphql';
import { parkingGql } from '../../common/gql/parking.gql';
import { totalSpacesGql } from '../../common/gql/total-spaces.gql';
import {
  Progress,
  ProgressGroup,
} from '../../components/progress/progress.component';

export type OccupancyDetailsProps = {
  widget?: {
    title?: string;
    icon?: IconType.Config.Type;
  };
  parkingId: string;
};

export const OccupancyDetails = observer(
  ({ parkingId, widget }: OccupancyDetailsProps) => {
    const { t } = useTranslation();

    const parking = useQuery<Pick<Query, 'parking'>, QueryParkingArgs>(
      parkingGql,
      {
        variables: { parkingId },
        socket: {
          'parkings:updated': (payload) => {
            return payload?.parking?.parkingId === parkingId;
          },
        },
      },
    );

    const { data } = useQuery<Pick<Query, 'totalSpaces'>, QueryTotalSpacesArgs>(
      totalSpacesGql,
      {
        variables: { parkingId },
        socket: {
          'contracts:updated': (payload) => {
            return payload?.contract?.parkingId === parkingId;
          },
          'contracts:created': (payload) => {
            return payload?.contract?.parkingId === parkingId;
          },
          'movements:updated': (payload) => {
            return true;
          },
        },
      },
    );

    const totalSpaces = parking?.data?.parking?.totalSpaces;
    const allocated = data?.totalSpaces?.allocated;
    const occupied = data?.totalSpaces?.occupied;

    return (
      <Widget
        config={{
          title: widget?.title,
          backtitle: !!widget?.title,
          icon: widget?.icon,
          padding: false,
        }}
      >
        <ProgressGroup config={{ total: totalSpaces }}>
          <Progress
            config={{
              name: (
                <Row
                  config={{
                    vertical: 'center',
                    horizontal: 'start',
                    gap: 4,
                  }}
                >
                  <Write
                    data={{ item: t('spacesAssignedIn') }}
                    config={{
                      mode: 'value-medium',
                      color: 'label',
                      wrap: false,
                    }}
                  />
                  <Icon config={{ type: 'customOSP', color: 'primary' }} />
                </Row>
              ),
              color: 'yang',
              value: totalSpaces || 0,
            }}
          />

          <Progress
            config={{
              name: t('spacesUnderContract'),
              color: 'success',
              value: allocated || 0,
            }}
          />

          <Progress
            config={{
              name: t('spacesCurrentlyOccupied'),
              color: 'error',
              value: occupied || 0,
            }}
          />
        </ProgressGroup>
      </Widget>
    );
  },
);
